/**
 * SlotPickerCalendar Component
 * 
 * Read-only, timezone-aware calendar for parents to select available slots.
 * Unlike the admin calendar, this is click-to-book only (no drag-to-create).
 * 
 * Features:
 * - Timezone selector with autocomplete (compact on mobile)
 * - Business hours highlighting (07:00-21:00)
 * - Available slots in blue, booked slots in green (tappable to cancel)
 * - Realtime updates via Supabase subscription
 * 
 * Styling: Uses schedule-calendar-light class from index.css for warm light theme
 */

import React, { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import type { EventInput, EventClickArg } from '@fullcalendar/core';
import { supabase } from '@/lib/supabase/client';
import { Loader2, Globe, ChevronLeft, ChevronRight, ChevronDown, Search } from 'lucide-react';
import { 
  SLOT_COLORS, 
  getSlotTypeLabel,
  PARENT_BUSINESS_HOURS,
  type AvailableSlot 
} from '@/lib/scheduling';
import { 
  getTimezoneLabel, 
  getTimezoneAbbreviation, 
  ALL_TIMEZONES, 
  TIMEZONE_GROUPS,
  utcOffsetMinutes,
} from '@/lib/timezones';
import { useAvailableSlots } from '@/hooks/useAvailableSlots';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface ChildScheduledClass {
  id: string;
  slot_id: string;
  status: string;
  slot: {
    id: string;
    start_time: string;
    end_time: string;
    slot_type: string;
    min_cancellation_notice_hours: number | null;
    instructors: { name: string } | null;
  } | null;
}

export interface BookedSlotInfo {
  scheduledClassId: string;
  slotId: string;
  startTime: string;
  endTime: string;
  slotType: string;
  instructorName?: string;
  minCancellationNoticeHours?: number;
}

interface SlotPickerCalendarProps {
  onSlotSelect: (slot: AvailableSlot) => void;
  childId: string;
  highlightHours?: { start: string; end: string };
  initialDate?: Date;
  existingSlotId?: string;
  existingBookings?: string[];
  timezone?: string;
  onTimezoneChange?: (tz: string) => void;
  onBookedSlotCancel?: (info: BookedSlotInfo) => void;
}

export const SlotPickerCalendar: React.FC<SlotPickerCalendarProps> = ({
  onSlotSelect,
  childId,
  highlightHours = PARENT_BUSINESS_HOURS,
  initialDate,
  existingSlotId,
  existingBookings = [],
  timezone: controlledTimezone,
  onTimezoneChange,
  onBookedSlotCancel,
}) => {
  const calendarRef = useRef<FullCalendar>(null);
  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const weekStripRef = useRef<HTMLDivElement>(null);
  const tzDropdownRef = useRef<HTMLDivElement>(null);
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [internalTimezone, setInternalTimezone] = useState(browserTimezone);
  const selectedTimezone = controlledTimezone ?? internalTimezone;
  const setSelectedTimezone = useCallback((tz: string) => {
    setInternalTimezone(tz);
    onTimezoneChange?.(tz);
  }, [onTimezoneChange]);

  const [showTzDropdown, setShowTzDropdown] = useState(false);
  const [tzSearch, setTzSearch] = useState('');

  const isMobile = useMediaQuery('(max-width: 639px)');

  // Close timezone dropdown on outside click
  useEffect(() => {
    if (!showTzDropdown) return;
    const handler = (e: MouseEvent) => {
      if (tzDropdownRef.current && !tzDropdownRef.current.contains(e.target as Node)) {
        setShowTzDropdown(false);
        setTzSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showTzDropdown]);

  const [selectedDate, setSelectedDate] = useState<Date>(initialDate || new Date());

  const weekDays = useMemo(() => {
    const d = new Date(selectedDate);
    const day = d.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const monday = new Date(d);
    monday.setDate(d.getDate() + mondayOffset);
    monday.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      return date;
    });
  }, [selectedDate]);

  const goToDay = useCallback((date: Date) => {
    setSelectedDate(date);
    const api = calendarRef.current?.getApi();
    if (api) api.gotoDate(date);
  }, []);

  const shiftWeek = useCallback((direction: 1 | -1) => {
    setSelectedDate(prev => {
      const next = new Date(prev);
      next.setDate(prev.getDate() + 7 * direction);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    const api = calendarRef.current?.getApi();
    if (api) api.gotoDate(selectedDate);
  }, [selectedDate, isMobile]);

  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    const currentDate = api.getDate();
    api.changeView(isMobile ? 'timeGridDay' : 'timeGridWeek', currentDate);
    if (isMobile) setSelectedDate(currentDate);
  }, [isMobile]);

  // Swipe on week strip only (mobile)
  useEffect(() => {
    const el = weekStripRef.current;
    if (!el || !isMobile) return;

    let startX = 0;
    let startY = 0;

    const onTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };

    const onTouchEnd = (e: TouchEvent) => {
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        shiftWeek(dx < 0 ? 1 : -1);
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [isMobile, shiftWeek]);

  const [childClasses, setChildClasses] = useState<ChildScheduledClass[]>([]);

  const slotDateRange = useMemo(() => {
    const start = new Date();
    const end = new Date(start);
    end.setDate(end.getDate() + 91);
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }, []);

  const { slots, loading, error, refetch } = useAvailableSlots({
    childId,
    startDate: slotDateRange.startDate,
    endDate: slotDateRange.endDate,
    enabled: true,
  });

  useEffect(() => {
    let isCancelled = false;
    
    const fetchChildClasses = async () => {
      if (!childId) return;
      
      const { data, error } = await supabase
        .from('scheduled_classes')
        .select(`
          id,
          slot_id,
          status,
          slot:class_slots (
            id,
            start_time,
            end_time,
            slot_type,
            min_cancellation_notice_hours,
            instructors (name)
          )
        `)
        .eq('child_id', childId)
        .in('status', ['scheduled', 'confirmed'])
        .gte('slot.start_time', new Date().toISOString());
      
      if (isCancelled) return;
      
      if (!error && data) {
        setChildClasses(data as ChildScheduledClass[]);
      }
    };
    
    fetchChildClasses();
    
    return () => {
      isCancelled = true;
    };
  }, [childId]);

  useEffect(() => {
    const channel = supabase
      .channel('slot-picker-updates')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'class_slots' },
        () => { refetch(); }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'scheduled_classes' },
        () => { refetch(); }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  // Compute timezone shift: adjusts UTC times so FC "local" mode displays them in selectedTimezone
  const adjustTime = useCallback((isoUtc: string): string => {
    if (selectedTimezone === browserTimezone) return isoUtc;
    if (!isoUtc.includes('T')) return isoUtc;
    const d = new Date(isoUtc);
    const browserOff = utcOffsetMinutes(browserTimezone, d);
    const selectedOff = utcOffsetMinutes(selectedTimezone, d);
    return new Date(d.getTime() + (selectedOff - browserOff) * 60000).toISOString();
  }, [selectedTimezone, browserTimezone]);

  const childBookedSlotIds = new Set(childClasses.map(c => c.slot_id));
  
  const availableSlotEvents: EventInput[] = slots.map((slot) => {
    const isBooked = childBookedSlotIds.has(slot.id) || existingBookings.includes(slot.id);
    const isCurrent = slot.id === existingSlotId;
    const isAvailable = slot.currentBookings < slot.maxCapacity;
    
    let backgroundColor: string;
    let borderColor: string;
    const textColor = '#1C1C1E';
    
    if (isCurrent) {
      backgroundColor = 'rgba(251, 191, 36, 0.3)';
      borderColor = '#FBBF24';
    } else if (isBooked) {
      backgroundColor = 'rgba(34, 197, 94, 0.3)';
      borderColor = '#22C55E';
    } else if (!isAvailable) {
      backgroundColor = 'rgba(107, 114, 128, 0.2)';
      borderColor = '#6B7280';
    } else {
      const colors = SLOT_COLORS[slot.slotType] || SLOT_COLORS.lesson;
      backgroundColor = colors.bg;
      borderColor = colors.border;
    }

    const scheduledClassId = isBooked
      ? childClasses.find(c => c.slot_id === slot.id)?.id
      : undefined;

    return {
      id: slot.id,
      title: `${getSlotTypeLabel(slot.slotType)}${slot.instructorName ? ` - ${slot.instructorName}` : ''}`,
      start: adjustTime(slot.startTime),
      end: adjustTime(slot.endTime),
      backgroundColor,
      borderColor,
      textColor,
      extendedProps: {
        slot,
        isBooked,
        isCurrent,
        isAvailable,
        scheduledClassId,
      },
    };
  });
  
  const availableSlotIds = new Set(slots.map(s => s.id));
  const bookedClassEvents: EventInput[] = childClasses
    .filter(c => c.slot && !availableSlotIds.has(c.slot_id))
    .map(c => {
      const slot = c.slot!;
      const isCurrent = c.slot_id === existingSlotId;
      const instructorName = slot.instructors?.name || 'TBD';
      
      return {
        id: `booked-${c.id}`,
        title: `${getSlotTypeLabel(slot.slot_type)} - ${instructorName}`,
        start: adjustTime(slot.start_time),
        end: adjustTime(slot.end_time),
        backgroundColor: isCurrent ? 'rgba(251, 191, 36, 0.3)' : 'rgba(34, 197, 94, 0.3)',
        borderColor: isCurrent ? '#FBBF24' : '#22C55E',
        textColor: '#1C1C1E',
        extendedProps: {
          slot: {
            id: slot.id,
            startTime: slot.start_time,
            endTime: slot.end_time,
            slotType: slot.slot_type,
            instructorName,
            currentBookings: 1,
            maxCapacity: 1,
            minCancellationNoticeHours: slot.min_cancellation_notice_hours ?? 24,
          },
          isBooked: true,
          isCurrent,
          isAvailable: false,
          scheduledClassId: c.id,
        },
      };
    });
  
  const events: EventInput[] = [...availableSlotEvents, ...bookedClassEvents];

  const { datesWithAvailable, datesWithBooked } = useMemo(() => {
    const available = new Set<string>();
    const booked = new Set<string>();
    for (const ev of events) {
      if (!ev.start) continue;
      const d = typeof ev.start === 'string' ? new Date(ev.start) : ev.start as Date;
      const key = d.toDateString();
      if (ev.extendedProps?.isBooked) {
        booked.add(key);
      } else {
        available.add(key);
      }
    }
    return { datesWithAvailable: available, datesWithBooked: booked };
  }, [events]);

  const handleEventClick = useCallback((info: EventClickArg) => {
    const { slot, isBooked, isCurrent, isAvailable, scheduledClassId } = info.event.extendedProps;
    
    if (isBooked && !isCurrent) {
      if (onBookedSlotCancel && scheduledClassId) {
        onBookedSlotCancel({
          scheduledClassId,
          slotId: slot.id,
          startTime: slot.startTime,
          endTime: slot.endTime,
          slotType: slot.slotType,
          instructorName: slot.instructorName,
          minCancellationNoticeHours: slot.minCancellationNoticeHours,
        });
      }
      return;
    }
    
    if (!isAvailable && !isCurrent) {
      return;
    }
    
    onSlotSelect(slot);
  }, [onSlotSelect, onBookedSlotCancel]);

  // Filtered timezone list for autocomplete
  const filteredTimezones = useMemo(() => {
    if (!tzSearch.trim()) return TIMEZONE_GROUPS;
    const q = tzSearch.toLowerCase();
    return TIMEZONE_GROUPS
      .map(group => ({
        ...group,
        timezones: group.timezones.filter(tz =>
          tz.label.toLowerCase().includes(q) || tz.value.toLowerCase().includes(q)
        ),
      }))
      .filter(group => group.timezones.length > 0);
  }, [tzSearch]);

  if (loading && slots.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#5C2D91]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96 text-red-600">
        <p>Error loading slots: {error}</p>
      </div>
    );
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const DAY_ABBR = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const weekLabel = (() => {
    const first = weekDays[0];
    const last = weekDays[6];
    const opts: Intl.DateTimeFormatOptions = { month: 'short' };
    if (first.getMonth() === last.getMonth()) {
      return `${first.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
    }
    return `${first.toLocaleDateString('en-US', opts)} – ${last.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
  })();

  const tzAbbr = getTimezoneAbbreviation(selectedTimezone);
  const tzLabel = getTimezoneLabel(selectedTimezone);

  return (
    <div className="space-y-4">
      {/* Header: Timezone + Legend */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {/* Timezone selector */}
        <div className="relative" ref={tzDropdownRef}>
          <button
            onClick={() => { setShowTzDropdown(!showTzDropdown); setTzSearch(''); }}
            className="flex items-center gap-2 px-3 py-2 sm:px-4 sm:py-2.5 rounded-xl text-sm bg-black/[0.03] border border-black/[0.08] hover:bg-black/[0.05] transition-colors"
          >
            <Globe className="w-4 h-4 text-[#8E8E93] flex-shrink-0" />
            {isMobile ? (
              <>
                <span className="text-[#1C1C1E] font-medium">{tzAbbr}</span>
                <ChevronDown className="w-3.5 h-3.5 text-[#AEAEB2]" />
              </>
            ) : (
              <>
                <span className="text-[#1C1C1E] font-medium">{tzLabel}</span>
                <ChevronDown className="w-3.5 h-3.5 text-[#AEAEB2]" />
              </>
            )}
          </button>

          {showTzDropdown && (
            <div
              className="absolute left-0 sm:left-auto sm:right-auto top-full mt-2 z-50 w-[min(320px,90vw)] max-h-72 rounded-xl border border-black/[0.08] shadow-lg overflow-hidden"
              style={{ background: 'rgba(250, 246, 240, 0.98)', backdropFilter: 'blur(20px)' }}
            >
              <div className="p-2 border-b border-black/[0.08]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#AEAEB2]" />
                  <input
                    type="text"
                    value={tzSearch}
                    onChange={(e) => setTzSearch(e.target.value)}
                    placeholder="Search timezone..."
                    autoFocus
                    className="w-full pl-9 pr-3 py-2 rounded-lg bg-black/[0.03] border border-black/[0.08] text-sm text-[#1C1C1E] placeholder-[#8E8E93] outline-none focus:border-[#5C2D91]"
                  />
                </div>
              </div>
              <div className="overflow-y-auto max-h-56">
                {filteredTimezones.map(group => (
                  <div key={group.region}>
                    <div className="px-3 py-1.5 text-[10px] font-semibold text-[#8E8E93] uppercase tracking-wider sticky top-0 bg-[#FAF6F0]">
                      {group.region}
                    </div>
                    {group.timezones.map(tz => (
                      <button
                        key={tz.value}
                        onClick={() => {
                          setSelectedTimezone(tz.value);
                          setShowTzDropdown(false);
                          setTzSearch('');
                        }}
                        className={`w-full px-3 py-2 text-left text-sm transition-colors min-h-[40px] ${
                          selectedTimezone === tz.value
                            ? 'bg-[#5C2D91]/10 text-[#5C2D91]'
                            : 'text-[#636366] hover:bg-black/[0.05]'
                        }`}
                      >
                        <span>{tz.label}</span>
                        <span className="ml-2 text-xs text-[#8E8E93]">
                          {getTimezoneAbbreviation(tz.value)}
                        </span>
                      </button>
                    ))}
                  </div>
                ))}
                {filteredTimezones.length === 0 && (
                  <div className="px-3 py-4 text-sm text-[#8E8E93] text-center">No timezones found</div>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-4 text-xs flex-wrap">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: SLOT_COLORS.lesson.border }} />
            <span className="text-[#636366]">Available</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-green-500" />
            <span className="text-[#636366]">Your booking</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-[#AEAEB2]" />
            <span className="text-[#636366]">Unavailable</span>
          </div>
        </div>
      </div>

      {/* Mobile: Week day-strip */}
      {isMobile && (
        <div ref={weekStripRef} className="rounded-xl bg-black/[0.02] border border-black/[0.08] overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-black/[0.06]">
            <button
              onClick={() => shiftWeek(-1)}
              className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-[#8E8E93] hover:text-[#1C1C1E] hover:bg-black/[0.05] transition-colors"
              aria-label="Previous week"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm font-medium text-[#1C1C1E]">{weekLabel}</span>
            <button
              onClick={() => shiftWeek(1)}
              className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-[#8E8E93] hover:text-[#1C1C1E] hover:bg-black/[0.05] transition-colors"
              aria-label="Next week"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0 px-1 py-2">
            {weekDays.map((day) => {
              const isSelected = isSameDay(day, selectedDate);
              const isToday = isSameDay(day, today);
              const dayKey = day.toDateString();
              const hasBooked = datesWithBooked.has(dayKey);
              const hasAvailable = datesWithAvailable.has(dayKey);
              const hasSlots = hasBooked || hasAvailable;
              return (
                <button
                  key={day.toISOString()}
                  onClick={() => goToDay(day)}
                  className={`flex flex-col items-center py-2 rounded-xl transition-colors min-h-[44px] ${
                    isSelected
                      ? 'bg-[#5C2D91] text-white'
                      : isToday
                        ? 'bg-black/[0.08] text-[#1C1C1E] ring-1 ring-[#5C2D91]/30'
                        : 'text-[#636366] hover:bg-black/[0.05]'
                  }`}
                >
                  <span className="text-[10px] font-medium uppercase leading-none mb-1">
                    {DAY_ABBR[day.getDay()]}
                  </span>
                  <span className="text-sm font-semibold leading-none">
                    {day.getDate()}
                  </span>
                  {hasSlots && (
                    <div className={`w-1 h-1 rounded-full mt-1 ${
                      isSelected ? 'bg-white/70' : hasBooked ? 'bg-[#22C55E]' : 'bg-[#5C2D91]'
                    }`} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Calendar time grid */}
      <div
        ref={calendarContainerRef}
        className="schedule-calendar-light slot-picker-mobile rounded-xl overflow-hidden bg-black/[0.02] border border-black/[0.08]"
        style={{ overflowX: 'hidden', touchAction: 'pan-y' }}
      >
        <div className="max-h-[60vh] sm:max-h-[70vh] overflow-y-auto overflow-x-hidden">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView={isMobile ? 'timeGridDay' : 'timeGridWeek'}
            initialDate={initialDate}
            timeZone="local"
            headerToolbar={isMobile
              ? false
              : {
                  left: 'prev,next today',
                  center: 'title',
                  right: 'timeGridWeek,timeGridDay',
                }
            }
            events={events}
            eventClick={handleEventClick}
            selectable={false}
            editable={false}
            slotMinTime="06:00"
            slotMaxTime="22:00"
            scrollTime={highlightHours.start}
            allDaySlot={false}
            nowIndicator={true}
            height="auto"
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short',
            }}
            slotLabelFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short',
            }}
            eventContent={(arg) => {
              const { isBooked, isCurrent, isAvailable } = arg.event.extendedProps;
              const isClickable = isAvailable || isCurrent || (isBooked && !!onBookedSlotCancel);
              return (
                <div 
                  className={`p-1 text-xs overflow-hidden pointer-events-none ${!isClickable ? 'opacity-50' : ''}`}
                >
                  <div className="font-medium truncate text-[#1C1C1E]">{arg.event.title}</div>
                  <div className="text-[10px] text-[#636366]">
                    {isCurrent && '(Current)'}
                    {isBooked && !isCurrent && '✓ Booked'}
                  </div>
                </div>
              );
            }}
            eventClassNames={(arg) => {
              const { isAvailable, isCurrent, isBooked } = arg.event.extendedProps;
              if (isAvailable || isCurrent) return 'cursor-pointer';
              if (isBooked && onBookedSlotCancel) return 'cursor-pointer';
              return 'cursor-not-allowed';
            }}
          />
        </div>
      </div>

      <p className="text-center text-sm text-[#8E8E93]">
        {isMobile
          ? 'Tap a day above, then a slot below'
          : onBookedSlotCancel
            ? 'Tap a slot to book · Tap a green slot to cancel'
            : 'Tap a slot to select · Swipe to change dates'}
      </p>
    </div>
  );
};

export default SlotPickerCalendar;
